import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, useInView } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { fetchTestimonials, addTestimonial, selectTestimonials, selectTestimonialsLoading } from '../redux/slices/testimonialSlice';

// Add these states:
import { fetchFeaturedProperties, selectFeaturedProperties, selectPropertyLoading } from '../redux/slices/propertySlice';
import { agentAPI } from '../services/api';

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const featuredProperties = useSelector(selectFeaturedProperties);
  const loading = useSelector(selectPropertyLoading);

  const [agents, setAgents] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [stats, setStats] = useState({
    totalProperties: 150,
    happyClients: 100,
    propertiesSold: 100,
    yearsExperience: 15
  });

  const [searchForm, setSearchForm] = useState({
    propertyType: '',
    city: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    bathrooms: '',
    status: 'sale',
    keyword: ''
  });

  // Animation refs
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const propertiesRef = useRef(null);
  const agentsRef = useRef(null);

  const isStatsInView = useInView(statsRef, { once: true });
  const isPropertiesInView = useInView(propertiesRef, { once: true });
  const isAgentsInView = useInView(agentsRef, { once: true });
  useEffect(() => {
  dispatch(fetchFeaturedProperties(6));
  dispatch(fetchTestimonials()); // ✅ ADD THIS
  fetchAgents();
  animateStats();
}, [dispatch]);

const [showReviewForm, setShowReviewForm] = useState(false);
const [reviewForm, setReviewForm] = useState({
  name: '',
  role: '',
  content: '',
  rating: 5,
  image: '👤'
});

// Handle form input
const handleReviewChange = (e) => {
  const { name, value } = e.target;
  setReviewForm(prev => ({ ...prev, [name]: value }));
};

// Submit review
const handleReviewSubmit = async (e) => {
  e.preventDefault();
  if (!reviewForm.name || !reviewForm.content) {
    toast.error('Please fill in all required fields');
    return;
  }
  try {
    await dispatch(addTestimonial(reviewForm)).unwrap();
    toast.success('Thank you for your review!');
    setShowReviewForm(false);
    setReviewForm({ name: '', role: '', content: '', rating: 5, image: '👤' });
  } catch (err) {
    toast.error('Failed to submit review');
  }
};

// Get testimonials from Redux state (add this with other selectors):
const testimonials = useSelector(selectTestimonials);
const testimonialsLoading = useSelector(selectTestimonialsLoading);


  useEffect(() => {
    dispatch(fetchFeaturedProperties(6));
    fetchAgents();
    animateStats();
  }, [dispatch]);

  const fetchAgents = async () => {
    try {
      const response = await agentAPI.getAll({ limit: 4 });
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const animateStats = () => {
    if (isStatsInView) {
      const duration = 2000;
      const increment = 50;
      const interval = duration / increment;

      Object.keys(stats).forEach(key => {
        let current = 0;
        const target = stats[key];
        const step = target / increment;

        const timer = setInterval(() => {
          current += step;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }
          setStats(prev => ({
            ...prev,
            [key]: Math.floor(current)
          }));
        }, interval);
      });
    }
  };
  const getPropertyImage = (property) => {
      // Check if property has imageUrl (processed by Redux)
      if (property.imageUrl) {
        return property.imageUrl;
      }
      
      // Check if property has images array
      if (property.images && property.images.length > 0) {
        const firstImage = property.images[0];
        // If it's an object with url property
        if (typeof firstImage === 'object' && firstImage.url) {
          return firstImage.url;
        }
        // If it's a string
        if (typeof firstImage === 'string') {
          return firstImage.startsWith('http') 
            ? firstImage 
            : `${process.env.REACT_APP_API_URL}/uploads/properties/${firstImage}`;
        }
      }
      
      // Check for other image properties
      if (property.featuredImage) {
        return property.featuredImage.startsWith('http') 
          ? property.featuredImage 
          : `${process.env.REACT_APP_API_URL}/uploads/properties/${property.featuredImage}`;
      }
      
      // Default fallback image
      return "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
    };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = () => {
    const queryParams = new URLSearchParams();
    Object.keys(searchForm).forEach(key => {
      if (searchForm[key]) queryParams.append(key, searchForm[key]);
    });

    if (Object.values(searchForm).every(value => !value)) {
      toast.error('Please enter at least one search criteria');
      return;
    }
    navigate(`/properties?${queryParams}`);
  };

  const clearFilters = () => {
    setSearchForm({
      propertyType: '',
      city: '',
      minPrice: '',
      maxPrice: '',
      bedrooms: '',
      bathrooms: '',
      status: 'sale',
      keyword: ''
    });
  };
  const [isMobile, setIsMobile] = useState(false);

useEffect(() => {
  const handleResize = () => setIsMobile(window.innerWidth < 768);
  handleResize(); // run on load
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

const heroImageDesktop = "https://i.pinimg.com/736x/a9/fe/fe/a9fefe0767b468591389c5d87eb581d0.jpg"; // 🖥️ your current hero image
const heroImageMobile = "images/bg.jpeg";   // 📱 new image for mobile
const heroImageToShow = isMobile ? heroImageMobile : heroImageDesktop;


  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const popularSearches = [
    { label: 'Luxury Homes', query: 'propertyType=house&minPrice=1000000' },
    { label: 'Downtown Apartments', query: 'propertyType=apartment&city=downtown' },
    { label: 'Family Houses', query: 'propertyType=house&bedrooms=3' },
    { label: 'New Construction', query: 'keyword=new construction' },
    { label: 'Waterfront Properties', query: 'keyword=waterfront' },
    { label: 'Investment Properties', query: 'keyword=investment' }
  ];

  

  // Pull a hero image from the first featured property (or fallback)
  const heroImage =
    "https://i.pinimg.com/736x/a9/fe/fe/a9fefe0767b468591389c5d87eb581d0.jpg"

  return (
    <div className="min-h-screen">
      {/* Hero Section - full bleed image with overlay listing panel */}
      <section
        ref={heroRef}
        className="relative min-h-[80vh] flex items-end"
      >
        <div
          className="absolute inset-0 bg-cover bg-center "
          style={{backgroundImage: `url(${heroImageToShow})`,width:'100%',
          
 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-8 pt-24">
            {/* Listing overlay */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="max-w-3xl text-white"
            >
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="rounded-full bg-blue-600/90 px-3 py-1 text-sm font-semibold shadow">
                  {featuredProperties?.listingType === 'rent' ? '🔑 For Rent' : '🏡 For Sale'}
                </span>
                {featuredProperties?.featured && (
                  <span className="rounded-full bg-yellow-500/90 px-3 py-1 text-sm font-semibold shadow">
                    ⭐ Featured
                  </span>
                )}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight drop-shadow">
                Find Your
                <span className="block text-white">Dream Home</span>
              </h1>

              <p className="mt-4 text-lg sm:text-xl text-white/85 max-w-2xl">
                Discover exceptional properties with our premium real estate platform in the most desirable locations. 
              </p>

             

              {/* Quick links */}
              <div className="mt-8 flex flex-wrap gap-3">
                {popularSearches.slice(0, 4).map((search, i) => (
                  <Link
                    key={i}
                    to={`/properties?${search.query}`}
                    className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm border border-white/20 transition-colors"
                  >
                    {search.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Docked Search Bar */}
          <div className="w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 md:p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-gray-900">Find Your Home</h2>
                  <div className="hidden md:flex rounded-lg bg-gray-100 p-1">
                    {['sale', 'rent'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setSearchForm(prev => ({ ...prev, status: type }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                          searchForm.status === type ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {type === 'sale' ? 'Buy' : 'Rent'}
                      </button>
                    ))}
                  </div>
                </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 md:items-end">
  {/* Property Type */}
  <div className="md:col-span-2">
    <select
      name="propertyType"
      value={searchForm.propertyType}
      onChange={handleInputChange}
      className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">All Types</option>
      <option value="house">House</option>
      <option value="apartment">Apartment</option>
      <option value="condo">Condo</option>
      <option value="townhouse">Townhouse</option>
      <option value="land">Land</option>
    </select>
  </div>

  {/* Location */}
  <div className="md:col-span-3">
    <input
      type="text"
      name="city"
      value={searchForm.city}
      onChange={handleInputChange}
      placeholder="City or neighborhood"
      className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>

  {/* Min Price */}
  <div className="md:col-span-2">
    <select
      name="minPrice"
      value={searchForm.minPrice}
      onChange={handleInputChange}
      className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">No Min</option>
      <option value="100000">₹1L+</option>
      <option value="250000">₹2.5L+</option>
      <option value="500000">₹5L+</option>
      <option value="750000">₹7.5L+</option>
      <option value="1000000">₹10L+</option>
    </select>
  </div>

  {/* Max Price */}
  <div className="md:col-span-2">
    <select
      name="maxPrice"
      value={searchForm.maxPrice}
      onChange={handleInputChange}
      className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">No Max</option>
      <option value="250000">₹2.5Lk</option>
      <option value="500000">₹5L</option>
      <option value="750000">₹7.5L</option>
      <option value="1000000">₹10L</option>
      <option value="2000000">₹20L</option>
    </select>
  </div>

  {/* Bedrooms */}
  <div className="md:col-span-1">
    <select
      name="bedrooms"
      value={searchForm.bedrooms}
      onChange={handleInputChange}
      className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">Beds</option>
      <option value="1">1+</option>
      <option value="2">2+</option>
      <option value="3">3+</option>
      <option value="4">4+</option>
      <option value="5">5+</option>
    </select>
  </div>

  {/* Bathrooms */}
  <div className="md:col-span-1">
    <select
      name="bathrooms"
      value={searchForm.bathrooms}
      onChange={handleInputChange}
      className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">Baths</option>
      <option value="1">1+</option>
      <option value="2">2+</option>
      <option value="3">3+</option>
      <option value="4">4+</option>
    </select>
  </div>

  {/* Search button */}
  <div className="md:col-span-1 flex items-stretch">
    <button
      onClick={handleSearch}
      className="w-full h-12 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      Search
    </button>
  </div>
</div>


                {/* Advanced filters toggle */}
                <div className="mt-3 flex items-center justify-between">
                  <div className="md:hidden rounded-lg bg-gray-100 p-1">
                    {['sale', 'rent'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setSearchForm(prev => ({ ...prev, status: type }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                          searchForm.status === type ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {type === 'sale' ? 'Buy' : 'Rent'}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      className="text-blue-600 font-semibold text-sm hover:text-blue-700"
                    >
                      {showAdvancedFilters ? 'Hide advanced' : 'Advanced filters'}
                    </button>
                    <button
                      onClick={clearFilters}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {showAdvancedFilters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 mt-3"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Keywords</label>
                      <input
                        type="text"
                        name="keyword"
                        value={searchForm.keyword}
                        onChange={handleInputChange}
                        placeholder="e.g., pool, garage, waterfront"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-end">
                      <Link
                        to="/properties"
                        className="w-full text-center px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all"
                      >
                        Browse All
                      </Link>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isStatsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { key: 'totalProperties', label: 'Properties Listed', icon: '🏘️' },
              { key: 'happyClients', label: 'Happy Clients', icon: '😊' },
              { key: 'propertiesSold', label: 'Properties Sold', icon: '✅' },
              { key: 'yearsExperience', label: 'Years Experience', icon: '⭐' }
            ].map((stat, index) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={isStatsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl mb-3">{stat.icon}</div>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stat.key === 'yearsExperience' ? stats[stat.key] : formatNumber(stats[stat.key])}
                  {stat.key === 'yearsExperience' ? '' : '+'}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Properties Section */}
      <section ref={propertiesRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isPropertiesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🌟 Featured Properties</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our handpicked selection of exceptional properties from our exclusive database
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-300"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                    <div className="h-6 bg-gray-300 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isPropertiesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="relative">
                    <img 
            src={getPropertyImage(property)}
            alt={property.title}
            className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
            }}
          />
                    <div className="absolute top-4 left-4 space-y-2">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">
                        {property.listingType === 'rent' ? 'For Rent' : 'For Sale'}
                      </span>
                      {property.featured && (
                        <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">
                          Featured
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                      {property.title}
                    </h3>
                    <p className="text-gray-600 mb-4 flex items-center">
                      <span className="mr-2">📍</span>{property.address}, {property.city}, {property.state}
                    </p>

                    <div className="flex items-center justify-between mb-4">
                      <div className="text-2xl font-extrabold text-blue-600">
                        {formatPrice(property.price)}
                        {property.listingType === 'rent' && <span className="text-base text-gray-500">/mo</span>}
                      </div>
                      <div className="text-sm text-gray-500">{property.squareFootage?.toLocaleString()} sq ft</div>
                    </div>

                    <div className="flex items-center gap-4 mb-5 text-sm text-gray-600">
                      <span className="flex items-center">🛏️ {property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}</span>
                      <span className="flex items-center">🚿 {property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''}</span>
                      <span className="flex items-center capitalize">🏠 {property.propertyType}</span>
                    </div>

                    <Link
                      to={`/properties/${property.id}`}
                      className="block w-full text-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all"
                    >
                      View Details
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={isPropertiesInView ? { opacity: 1 } : {}}
              className="text-center py-20 bg-white rounded-2xl shadow-lg"
            >
              <div className="text-6xl mb-4">🏠</div>
              <div className="space-y-3 mb-6">
                {Array.isArray(featuredProperties) && featuredProperties.length > 0 ? (
                  featuredProperties.map((property, index) => (
                    <div key={index} className="text-gray-800 font-semibold">
                      {property.title} – {property.city}, {property.state} ({formatPrice(property.price)})
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600">No featured properties available</p>
                )}
              </div>
              <Link
                to="/properties"
                className="inline-block px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                Browse All Properties
              </Link>
            </motion.div>
          )}

          {featuredProperties.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isPropertiesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center mt-12"
            >
              <Link
                to="/properties"
                className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl border-2 border-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow"
              >
                View All Properties →
              </Link>
            </motion.div>
          )}
        </div>
      </section>

{/* Testimonials Section */}
<section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="text-center mb-16"
    >
      <h2 className="text-4xl font-bold text-gray-900 mb-4">💬 What Our Clients Say</h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Join thousands of satisfied clients who found their dream properties through our premium platform
      </p>
      
      {/* Submit Review Button */}
      <button
        onClick={() => setShowReviewForm(!showReviewForm)}
        className="mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
      >
        {showReviewForm ? '✖ Cancel' : '✍️ Share Your Experience'}
      </button>
    </motion.div>

    {/* Review Form */}
    {showReviewForm && (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 mb-12"
      >
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Share Your Review</h3>
        <form onSubmit={handleReviewSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
              <input
                type="text"
                name="name"
                value={reviewForm.name}
                onChange={handleReviewChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Role</label>
              <input
                type="text"
                name="role"
                value={reviewForm.role}
                onChange={handleReviewChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Home Buyer, Investor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Your Review *</label>
            <textarea
              name="content"
              value={reviewForm.content}
              onChange={handleReviewChange}
              required
              rows="4"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Share your experience with us..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Rating</label>
              <select
                name="rating"
                value={reviewForm.rating}
                onChange={handleReviewChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="5">⭐⭐⭐⭐⭐ (5 stars)</option>
                <option value="4">⭐⭐⭐⭐ (4 stars)</option>
                <option value="3">⭐⭐⭐ (3 stars)</option>
                <option value="2">⭐⭐ (2 stars)</option>
                <option value="1">⭐ (1 star)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Avatar Emoji</label>
              <select
                name="image"
                value={reviewForm.image}
                onChange={handleReviewChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="👤">👤 Default</option>
                <option value="👨‍💼">👨‍💼 Male Professional</option>
                <option value="👩‍💼">👩‍💼 Female Professional</option>
                <option value="👨‍💻">👨‍💻 Male Tech</option>
                <option value="👩‍💻">👩‍💻 Female Tech</option>
                <option value="🧑‍🎓">🧑‍🎓 Student</option>
                <option value="👨‍🏫">👨‍🏫 Male Teacher</option>
                <option value="👩‍🏫">👩‍🏫 Female Teacher</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
          >
            Submit Review
          </button>
        </form>
      </motion.div>
    )}

    {/* Testimonials Display */}
    {testimonialsLoading ? (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 shadow-lg animate-pulse">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gray-300 rounded-full mr-3"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2"></div>
              </div>
            </div>
            <div className="h-3 bg-gray-300 rounded mb-2"></div>
            <div className="h-3 bg-gray-300 rounded mb-2"></div>
            <div className="h-3 bg-gray-300 rounded w-4/5"></div>
          </div>
        ))}
      </div>
    ) : testimonials.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonials.slice(0, 6).map((testimonial, index) => (
          <motion.div
            key={testimonial.id}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center mb-4">
              <div className="text-3xl mr-3">{testimonial.image || '👤'}</div>
              <div>
                <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                <p className="text-sm text-gray-600">{testimonial.role}</p>
              </div>
            </div>
            <div className="flex mb-4">
              {[...Array(Number(testimonial.rating) || 5)].map((_, i) => (
                <span key={i} className="text-yellow-500">⭐</span>
              ))}
            </div>
            <p className="text-gray-600 italic">"{testimonial.content}"</p>
          </motion.div>
        ))}
      </div>
    ) : (
      <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
        <div className="text-6xl mb-4">💬</div>
        <p className="text-gray-600 text-lg">No testimonials yet. Be the first to share your experience!</p>
      </div>
    )}
  </div>
</section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-white"
          >
            <h2 className="text-4xl font-bold mb-4">🚀 Ready to Find Your Dream Home?</h2>
            <p className="text-xl mb-8 opacity-90">
              Start your real estate journey today with our premium platform and expert guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-all shadow transform hover:scale-105"
              >
                🎯 Get Started Free
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white hover:bg-white hover:text-blue-600 transition-all"
              >
                💬 Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
